/**
 * Alert sounds made with Web Audio (no audio files to load) and spoken Vietnamese via the
 * browser's speech engine. Browsers keep audio locked until the user taps the page once,
 * so call `unlockAudio` from a click/tap handler before the first alert.
 */

type Note = { freq: number; at: number; length: number };

const CHIMES: Record<'order' | 'ready', Note[]> = {
  // "ding-dong" twice for a new order: loud and long enough to hear over a busy counter
  order: [
    { freq: 880, at: 0, length: 0.45 },
    { freq: 1175, at: 0.35, length: 0.8 },
    { freq: 880, at: 1.2, length: 0.45 },
    { freq: 1175, at: 1.55, length: 0.8 },
  ],
  // three bright notes: "your drink is ready"
  ready: [
    { freq: 1047, at: 0, length: 0.25 },
    { freq: 1319, at: 0.18, length: 0.25 },
    { freq: 1568, at: 0.36, length: 0.55 },
  ],
};

let context: AudioContext | null = null;

const audioContext = (): AudioContext | null => {
  if (typeof window === 'undefined' || typeof window.AudioContext === 'undefined') return null;
  context ??= new window.AudioContext();
  return context;
};

/** Must run inside a user gesture once per page load; later alerts can then play on their own. */
export const unlockAudio = (): void => {
  const ctx = audioContext();
  if (ctx && ctx.state === 'suspended') void ctx.resume();
  // an empty utterance unlocks speech on iOS Safari
  if (typeof speechSynthesis !== 'undefined' && !speechSynthesis.speaking) speechSynthesis.speak(new SpeechSynthesisUtterance(''));
};

/** False until the page has been tapped once (and on browsers without Web Audio). */
export const audioUnlocked = (): boolean => audioContext()?.state === 'running';

export const playChime = (kind: 'order' | 'ready'): void => {
  const ctx = audioContext();
  if (!ctx) return;
  const start = ctx.currentTime + 0.02;
  for (const note of CHIMES[kind]) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = note.freq;
    gain.gain.setValueAtTime(0.0001, start + note.at);
    gain.gain.exponentialRampToValueAtTime(0.9, start + note.at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + note.at + note.length);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start + note.at);
    osc.stop(start + note.at + note.length + 0.05);
  }
};

// Chrome fills the voice list asynchronously: the first getVoices() call is usually empty.
let voices: SpeechSynthesisVoice[] = [];
if (typeof speechSynthesis !== 'undefined') {
  voices = speechSynthesis.getVoices();
  speechSynthesis.addEventListener('voiceschanged', () => {
    voices = speechSynthesis.getVoices();
  });
}

const vietnameseVoice = (): SpeechSynthesisVoice | undefined => {
  if (voices.length === 0) voices = speechSynthesis.getVoices();
  return voices.find((v) => v.lang.toLowerCase().startsWith('vi'));
};

/** False when the browser lists no Vietnamese voice; `speak` still tries, and some devices manage anyway. */
export const canSpeakVietnamese = (): boolean => typeof speechSynthesis !== 'undefined' && vietnameseVoice() !== undefined;

export const speak = (text: string): void => {
  if (typeof speechSynthesis === 'undefined') return;
  const voice = vietnameseVoice();
  const utterance = new SpeechSynthesisUtterance(text);
  // without a listed voice, asking for vi-VN lets Android/iOS pick their built-in Vietnamese engine
  if (voice) utterance.voice = voice;
  utterance.lang = voice?.lang ?? 'vi-VN';
  utterance.rate = 0.95;
  speechSynthesis.speak(utterance);
};
